import { ArrowUp, MessageCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useAuth } from "./AuthProvider";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: false });
  const { user } = useAuth();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    message: ""
  });

  // Listen for open-contact-support event from navigation dropdown
  useEffect(() => {
    const handleOpenContactSupport = () => {
      if (user) {
        setIsContactOpen(true);
      }
    };

    window.addEventListener('open-contact-support', handleOpenContactSupport);
    return () => {
      window.removeEventListener('open-contact-support', handleOpenContactSupport);
    };
  }, [user]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.email) {
      toast({
        title: "Error",
        description: "Please log in to send a support request.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.name || !formData.subject || !formData.message) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-support-email', {
        body: {
          name: formData.name,
          email: user.email,
          subject: formData.subject,
          message: formData.message
        }
      });

      if (error) throw error;

      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible."
      });

      setFormData({ name: "", subject: "", message: "" });
      setIsContactOpen(false);
    } catch (error) {
      console.error('Error sending support email:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer ref={footerRef} className="border-t border-gray-800 mt-auto bg-gradient-to-b from-gray-900 to-gray-800 relative">
      {/* Floating Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        initial={{ opacity: 0, scale: 0 }}
        animate={{
          opacity: isInView ? 1 : 0,
          scale: isInView ? 1 : 0
        }}
        whileHover={{ scale: 1.1, rotate: 360 }}
        whileTap={{ scale: 0.9 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl border-2 border-white/20"
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-6 h-6 text-white" />
      </motion.button>

      <div className="container max-w-7xl mx-auto px-6 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 relative">
          {/* Brand Section */}
          <div className="md:col-span-1 relative z-10">
            <Link to="/" className="inline-block hover:opacity-80 transition-opacity duration-200 mb-4">
              <img
                src="/wtc-logo.png"
                alt="WillTheyConvert"
                width="195"
                height="80"
                className="h-14 sm:h-16 md:h-20 w-auto object-contain brightness-0 invert max-w-[320px]"
              />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed max-w-sm mb-6 pl-0">
              Stop guessing. Start testing. The market is talking — WillTheyConvert helps you listen.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Product</h4>
            <nav className="flex flex-col space-y-3">
              <Link to="/create" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Flow Builder
              </Link>
              <Link to="/features" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Flow Types
              </Link>
              <Link to="/dashboard" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Analytics Dashboard
              </Link>
              <Link to="/subscription" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Pricing
              </Link>
            </nav>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Resources</h4>
            <nav className="flex flex-col space-y-3">
              <Link to="/validate-startup" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                How It Works
              </Link>
              <Link to="/case-studies" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Founder Stories
              </Link>
            </nav>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Company</h4>
            <nav className="flex flex-col space-y-3">
              <Link to="/about" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Why We Built This
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-indigo-400 transition-colors duration-200">
                Privacy Policy
              </Link>
            </nav>

            {/* Contact Support - Only for logged in users */}
            {user && (
              <Dialog open={isContactOpen} onOpenChange={setIsContactOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="mt-6 bg-white text-gray-900 hover:bg-gray-100 border-white/20 rounded-full px-6 py-2 font-medium transition-all duration-200 gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Contact Support
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Contact Support</DialogTitle>
                    <DialogDescription>
                      Send us a message and we'll get back to you as soon as possible.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleContactSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Your Email</Label>
                      <Input
                        id="email"
                        value={user.email || ""}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        placeholder="How can we help?"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us more about your issue or question..."
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        disabled={isSubmitting}
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Featured Badges */}
        <div className="border-t border-gray-700 pt-12 mb-12">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <motion.div
              whileHover={{ y: -2, scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <a href="https://startupfa.me/s/willtheyconvert.com?utm_source=willtheyconvert.com" target="_blank" rel="noreferrer" className="block bg-gray-800/50 rounded-2xl border border-gray-700 p-4 hover:bg-gray-800/70 hover:border-indigo-500 transition-all">
                <img src="https://startupfa.me/badges/featured/default-small.webp" alt="Featured on Startup Fame" width="120" height="40" loading="lazy" className="h-10 w-auto object-contain" />
              </a>
            </motion.div>
            <motion.div
              whileHover={{ y: -2, scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <a href="https://fazier.com/launches/willtheyconvert-2" target="_blank" rel="noreferrer" className="block bg-gray-800/50 rounded-2xl border border-gray-700 p-4 hover:bg-gray-800/70 hover:border-indigo-500 transition-all">
                <img src="https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=3959&badge_type=weekly&theme=light" alt="Fazier badge" width="100" height="40" loading="lazy" className="h-10 w-auto object-contain" />
              </a>
            </motion.div>
            <motion.div
              whileHover={{ y: -2, scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <a href="https://dang.ai/" target="_blank" rel="noreferrer" className="block bg-gray-800/50 rounded-2xl border border-gray-700 p-4 hover:bg-gray-800/70 hover:border-indigo-500 transition-all">
                <img src="https://cdn.prod.website-files.com/63d8afd87da01fb58ea3fbcb/6487e2868c6c8f93b4828827_dang-badge.png" alt="Dang.ai" width="100" height="40" loading="lazy" className="h-10 w-auto object-contain" />
              </a>
            </motion.div>
            <motion.div
              whileHover={{ y: -2, scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <a href="https://twelve.tools" target="_blank" rel="noreferrer" className="block bg-gray-800/50 rounded-2xl border border-gray-700 p-4 hover:bg-gray-800/70 hover:border-indigo-500 transition-all">
                <img src="https://twelve.tools/badge0-dark.svg" alt="Featured on Twelve Tools" width="100" height="40" loading="lazy" className="h-10 w-auto object-contain" />
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Social Links */}
            <div className="flex items-center gap-6">
              <a
                href="https://x.com/willtheyconvert"
                target="_blank"
                rel="noreferrer"
                className="text-gray-400 hover:text-indigo-400 transition-colors duration-200 hover:scale-110 transform"
                aria-label="Follow on X"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>

            {/* Copyright */}
            <div className="text-sm text-gray-400">
              <p>© {currentYear} WillTheyConvert — Validation tools for founders who build things people actually want.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}; 
 
 
